import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { catchError, debounceTime, takeUntil, tap } from 'rxjs/operators';
import { CHAT_CONSTANTS } from '../config/chat.constants';
import { useUser } from '../core/UserContext';
import { AssistantVariant, Message, PdfDocument } from '../models/models';
import { conversationService, UpdateSourcesParams } from '../services/conversation.service';
import { parseError } from '../services/http.service';
import { streamingService, StreamMessageParams } from '../services/streaming.service';
import { extractCitations } from '../utils/chat-utils';
import { useSnackbar } from './SnackbarContext';

export interface ConversationState {
  conversationId: string | null;
  messages: Message[];
  answerVariants: AssistantVariant[];
  selectedGroupIds: number[];
  selectedDocIds: number[];
  selectedModelId: number | null;
  searchInternet: boolean;
  isGenerating: boolean;
  isLoading: boolean;
  documents: PdfDocument[];
}

interface ConversationContextValue {
  state: ConversationState;
  sendMessage: (text: string, files: File[] | null, searchInternet: boolean) => Promise<void>;
  retryMessage: () => Promise<void>;
  retryInNewChat: () => Promise<void>;
  stopGeneration: () => void;
  updateSourcesFromUser: (groupIds: number[], docIds: number[]) => Promise<void>;
  updateCollectionsFromUser: (groupIds: number[]) => Promise<void>;
  updateDocumentsFromUser: (docIds: number[]) => Promise<void>;
  updateModelFromUser: (modelId: number) => Promise<void>;
  loadConversationFromBackend: (id: string) => Promise<void>;
  createNewConversation: () => void;
  activateVariant: (variantId: number) => Promise<void>;
  exportToPdf: () => Promise<void>;
  setInitialModel: (modelId: number) => void;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within ConversationProvider');
  }
  return context;
};

export const ConversationProvider: React.FC<{ children: React.ReactNode; conversationId: string | null }> = ({
  children,
  conversationId: urlConversationId,
}) => {
  const { showSnackbar } = useSnackbar();
  const { triggerConversationRefresh } = useUser();
  const navigate = useNavigate();

  const messages$ = useRef(new BehaviorSubject<Message[]>([])).current;
  const answerVariants$ = useRef(new BehaviorSubject<AssistantVariant[]>([])).current;
  const selectedGroupIds$ = useRef(new BehaviorSubject<number[]>([])).current;
  const selectedDocIds$ = useRef(new BehaviorSubject<number[]>([])).current;
  const selectedModelId$ = useRef(new BehaviorSubject<number | null>(null)).current;
  const searchInternet$ = useRef(new BehaviorSubject<boolean>(false)).current;
  const isGenerating$ = useRef(new BehaviorSubject<boolean>(false)).current;
  const isLoading$ = useRef(new BehaviorSubject<boolean>(false)).current;
  const conversationId$ = useRef(new BehaviorSubject<string | null>(null)).current;
  const documents$ = useRef(new BehaviorSubject<PdfDocument[]>([])).current;

  const stopGeneration$ = useRef(new Subject<void>()).current;
  const operationSeqRef = useRef(0);
  const isLoadingFromBackendRef = useRef(false);
  const prevUrlConversationIdRef = useRef<string | null>(null);

  const [state, setState] = useState<ConversationState>({
    conversationId: null,
    messages: [],
    answerVariants: [],
    selectedGroupIds: [],
    selectedDocIds: [],
    selectedModelId: null,
    searchInternet: false,
    isGenerating: false,
    isLoading: false,
    documents: [],
  });

  useEffect(() => {
    const subscription = combineLatest([
      messages$,
      answerVariants$,
      selectedGroupIds$,
      selectedDocIds$,
      selectedModelId$,
      searchInternet$,
      isGenerating$,
      isLoading$,
      conversationId$,
      documents$,
    ])
      .pipe(debounceTime(CHAT_CONSTANTS.STATE_DEBOUNCE_MS))
      .subscribe(
        ([
          messages,
          answerVariants,
          selectedGroupIds,
          selectedDocIds,
          selectedModelId,
          searchInternet,
          isGenerating,
          isLoading,
          conversationId,
          documents,
        ]) => {
          setState({
            messages,
            answerVariants,
            selectedGroupIds,
            selectedDocIds,
            selectedModelId,
            searchInternet,
            isGenerating,
            isLoading,
            conversationId,
            documents,
          });
        }
      );

    return () => subscription.unsubscribe();
  }, []);

  const loadConversationFromBackend = useCallback(
    async (id: string, skipIfGenerating = false) => {
      if (skipIfGenerating && isGenerating$.value) {
        return;
      }

      isLoadingFromBackendRef.current = true;
      isLoading$.next(true);

      try {
        const data = await conversationService.fetchConversation(id);

        conversationId$.next(String(data.id));
        messages$.next(data.messages);
        answerVariants$.next(data.answerVariants);
        selectedGroupIds$.next(data.selectedGroupIds);
        selectedDocIds$.next(data.selectedDocIds);

        if (data.selectedModelId) {
          selectedModelId$.next(data.selectedModelId);
        }

        searchInternet$.next(true);
        documents$.next(data.documents);
      } catch (error) {
        showSnackbar(parseError(error), 'error');
      } finally {
        isLoading$.next(false);
        isLoadingFromBackendRef.current = false;
      }
    },
    [showSnackbar]
  );

  const createNewConversation = useCallback(() => {
    conversationId$.next(null);
    messages$.next([]);
    answerVariants$.next([]);
    documents$.next([]);
  }, []);

  const ensureConversationExists = useCallback(async (): Promise<string> => {
    const currentId = conversationId$.value;
    const normalizedId = currentId === undefined ? null : currentId;

    if (normalizedId) {
      return normalizedId;
    }

    const newId = await conversationService.createConversation({});
    conversationId$.next(newId);

    if (selectedModelId$.value != null) {
      await conversationService.updateModel(newId, selectedModelId$.value);
    }

    if (selectedGroupIds$.value.length > 0 || selectedDocIds$.value.length > 0) {
      await conversationService.updateSources(newId, {
        selectedGroupIds: selectedGroupIds$.value,
        selectedDocIds: selectedDocIds$.value,
      });
    }

    navigate(`/chat/${newId}`);
    return newId;
  }, [navigate]);

  const updateSourcesFromUser = useCallback(
    async (groupIds: number[], docIds: number[]) => {
      if (isLoadingFromBackendRef.current) return;

      selectedGroupIds$.next(groupIds);
      selectedDocIds$.next(docIds);

      const currentId = conversationId$.value;
      if (!currentId) return;

      try {
        const params: UpdateSourcesParams = {
          selectedGroupIds: groupIds,
          selectedDocIds: docIds,
        };
        await conversationService.updateSources(currentId, params);
      } catch (error) {
        showSnackbar(parseError(error), 'error');
      }
    },
    [showSnackbar]
  );

  const updateCollectionsFromUser = useCallback(
    async (groupIds: number[]) => {
      if (isLoadingFromBackendRef.current) return;

      selectedGroupIds$.next(groupIds);

      const currentId = conversationId$.value;
      if (!currentId) return;

      try {
        if (groupIds?.length >= 0) {
          await axios.patch(`/chat/conversation/${currentId}/collections`, groupIds);
        }
      } catch (error) {
        showSnackbar(parseError(error), 'error');
      }
    },
    [showSnackbar]
  );

  const updateDocumentsFromUser = useCallback(
    async (docIds: number[]) => {
      if (isLoadingFromBackendRef.current) return;

      selectedDocIds$.next(docIds);

      const currentId = conversationId$.value;
      if (!currentId) return;

      try {
        if (docIds?.length >= 0) {
          await axios.patch(`/chat/conversation/${currentId}/documents`, {
            documentIds: docIds
          });
        }
      } catch (error) {
        showSnackbar(parseError(error), 'error');
      }
    },
    [showSnackbar]
  );

  const updateModelFromUser = useCallback(
    async (modelId: number) => {
      if (isLoadingFromBackendRef.current) return;

      selectedModelId$.next(modelId);

      const currentId = conversationId$.value;
      if (!currentId) return;

      try {
        await conversationService.updateModel(currentId, modelId);
      } catch (error) {
        showSnackbar(parseError(error), 'error');
      }
    },
    [showSnackbar]
  );

  const handleStreamingResponse = useCallback(
    (params: StreamMessageParams, mySeq: number, conversationId: string) => {
      let accumulatedText = '';

      streamingService
        .createMessageStream(params)
        .pipe(
          takeUntil(stopGeneration$),
          tap((chunk) => {
            if (mySeq !== operationSeqRef.current) return;

            if (chunk.includes('CITATIONS:')) {
              return;
            }

            accumulatedText += chunk;

            const currentMessages = messages$.value;
            const updated = [...currentMessages];
            const lastIdx = updated.length - 1;

            if (lastIdx >= 0 && updated[lastIdx].isSystem) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                  streamingText: accumulatedText,
                  text: accumulatedText,
                  isLoading: false,
              };
            }

            messages$.next(updated);
          }),
          catchError((error) => {
            if (mySeq === operationSeqRef.current) {
              showSnackbar(parseError(error), 'error');
              isGenerating$.next(false);
            }
            return of(null);
          })
        )
        .subscribe({
          complete: async () => {
            if (mySeq !== operationSeqRef.current) {
              isGenerating$.next(false);
              return;
            }

            isGenerating$.next(false);

            try {
              const data = await conversationService.fetchConversation(conversationId);

              answerVariants$.next(data.answerVariants);
              documents$.next(data.documents);

              const currentMessages = messages$.value;
              if (currentMessages.length > 0) {
                const lastIdx = currentMessages.length - 1;
                const lastMsg = currentMessages[lastIdx];

                if (lastMsg.isSystem && data.messages.length > 0) {
                  const lastBackendMsg = data.messages[data.messages.length - 1];

                  if (lastBackendMsg.isSystem) {
                    const sourceText = lastMsg.streamingText ?? lastMsg.text ?? '';
                    const { citations } = extractCitations(sourceText);

                    const updated = [...currentMessages];
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      text: lastBackendMsg.text,
                      timestamp: lastBackendMsg.timestamp,
                      documents: lastBackendMsg.documents,
                      citations: citations.length > 0 ? citations : undefined,
                      isLoading: false,
                    };
                    messages$.next(updated);
                  }
                }
              }

              triggerConversationRefresh();
            } catch (error) {
              showSnackbar(parseError(error), 'error');
            }
          },
        });
    },
    [showSnackbar, triggerConversationRefresh]
  );

  const sendMessage = useCallback(
    async (text: string, files: File[] | null, searchInternet: boolean) => {
      try {
        const conversationId = await ensureConversationExists();
        const mySeq = ++operationSeqRef.current;

        searchInternet$.next(searchInternet);

        const userMessage: Message = {
          author: 'USER',
          text,
          isSystem: false,
          id: `user-${Date.now()}-${Math.random()}`,
          timestamp: new Date().toISOString(),
          documents: files && files.length > 0 ? files.map(f => ({
            documentId: -1,
            documentName: f.name
          })) : undefined
        };

        const loadingMessage: Message = {
          author: 'ASSISTANT',
          text: '',
          isSystem: true,
          isLoading: true,
          id: `assistant-${Date.now()}-${Math.random()}`,
        };

        answerVariants$.next([]);
        messages$.next([...messages$.value, userMessage, loadingMessage]);
        isGenerating$.next(true);

        const params: StreamMessageParams = {
          conversationId,
          question: text,
          files,
          searchInternet,
        };

        handleStreamingResponse(params, mySeq, conversationId);
      } catch (error) {
        showSnackbar(parseError(error), 'error');
        isGenerating$.next(false);
      }
    },
    [ensureConversationExists, showSnackbar, handleStreamingResponse]
  );

  const stopGeneration = useCallback(async () => {
    stopGeneration$.next();
    isGenerating$.next(false);

    const currentMessages = messages$.value;
    if (currentMessages.length > 0) {
      const lastIdx = currentMessages.length - 1;
      if (currentMessages[lastIdx].isSystem && currentMessages[lastIdx].isLoading) {
        const updated = [...currentMessages];
        updated[lastIdx] = {
          ...updated[lastIdx],
          isLoading: false,
        };
        messages$.next(updated);
      }
    }

    const currentId = conversationId$.value;
    if (currentId) {
      await new Promise(resolve => setTimeout(resolve, CHAT_CONSTANTS.STREAM_COMPLETE_DELAY_MS));

      conversationService.cancelGeneration(parseInt(currentId, 10)).catch(() => undefined);
    }
  }, []);

  const retryMessage = useCallback(async () => {
    const lastUserMsg = messages$.value
      .slice()
      .reverse()
      .find((m) => !m.isSystem);

    if (!lastUserMsg) return;

    const currentId = conversationId$.value;
    if (!currentId) return;

    const mySeq = ++operationSeqRef.current;

    answerVariants$.next([]);

    const updated = [...messages$.value];
    for (let i = updated.length - 1; i >= 0; i--) {
      if (updated[i].isSystem) {
        updated[i] = {
          author: 'ASSISTANT',
          text: '',
          isSystem: true,
          isLoading: true,
        };
        break;
      }
    }
    messages$.next(updated);
    isGenerating$.next(true);

    try {
      const params: StreamMessageParams = {
        conversationId: currentId,
        question: lastUserMsg.text,
        files: null,
        searchInternet: searchInternet$.value,
        retry: true,
      };

      handleStreamingResponse(params, mySeq, currentId);
    } catch (error) {
      showSnackbar(parseError(error), 'error');
      isGenerating$.next(false);
    }
  }, [showSnackbar, handleStreamingResponse]);

  const retryInNewChat = useCallback(async () => {
    const lastUserMsg = messages$.value
      .slice()
      .reverse()
      .find((m) => !m.isSystem);

    if (!lastUserMsg) return;

    const currentGroupIds = selectedGroupIds$.value;
    const currentDocIds = selectedDocIds$.value;
    const currentModelId = selectedModelId$.value;
    const currentSearchInternet = searchInternet$.value;

    conversationId$.next(null);
    messages$.next([]);
    answerVariants$.next([]);
    documents$.next([]);

    setTimeout(() => {
        selectedGroupIds$.next(currentGroupIds);
        selectedDocIds$.next(currentDocIds);
        selectedModelId$.next(currentModelId);
        searchInternet$.next(currentSearchInternet);
    });

    navigate('/chat');

    await new Promise(resolve => setTimeout(resolve, CHAT_CONSTANTS.STREAM_COMPLETE_DELAY_MS));

    await sendMessage(lastUserMsg.text, null, currentSearchInternet);
  }, [sendMessage, navigate]);

  const activateVariant = useCallback(
    async (variantId: number) => {
      try {
        await conversationService.activateVariant(variantId);

        const updated = answerVariants$.value.map((v) => ({
          ...v,
          active: v.id === variantId,
        }));
        answerVariants$.next(updated);

        const activeVariant = updated.find((v) => v.id === variantId);
        if (activeVariant) {
          const messagesUpdated = [...messages$.value];
          for (let i = messagesUpdated.length - 1; i >= 0; i--) {
            if (messagesUpdated[i].isSystem) {
              messagesUpdated[i] = {
                ...messagesUpdated[i],
                text: activeVariant.content,
                documents: activeVariant.documents,
              };
              break;
            }
          }
          messages$.next(messagesUpdated);
        }

      } catch (error) {
        showSnackbar(parseError(error), 'error');
      }
    },
    [showSnackbar]
  );

  const exportToPdf = useCallback(async () => {
    const currentId = conversationId$.value;
    if (!currentId) return;

    try {
      const blob = await conversationService.exportToPdf(currentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conversation-${currentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Eksport zakończony', 'success');
    } catch (error) {
      showSnackbar(parseError(error), 'error');
    }
  }, [showSnackbar]);

  const setInitialModel = useCallback((modelId: number) => {
    if (!conversationId$.value) {
      selectedModelId$.next(modelId);
    }
  }, []);

  useEffect(() => {
    const prev = prevUrlConversationIdRef.current;
    const curr = urlConversationId;

    if (prev !== curr && prev !== null && isGenerating$.value) {
      stopGeneration$.next();
      operationSeqRef.current++;
      isGenerating$.next(false);

      const prevId = parseInt(prev, 10);
      if (!isNaN(prevId)) {
        void conversationService.cancelGeneration(prevId).catch(() => undefined);
      }
    }

    if (curr && curr !== prev && !isGenerating$.value) {
      void loadConversationFromBackend(curr);
    }

    if (!curr && prev && !isGenerating$.value) {
      conversationId$.next(null);
      messages$.next([]);
      answerVariants$.next([]);
      documents$.next([]);
    }

    prevUrlConversationIdRef.current = curr;
  }, [urlConversationId, loadConversationFromBackend]);

  useEffect(() => {
    return () => {
      if (isGenerating$.value) {
        stopGeneration$.next();

        const currentId = conversationId$.value;
        if (currentId) {
          const numericId = parseInt(currentId, 10);
          if (!isNaN(numericId)) {
            void conversationService.cancelGeneration(numericId).catch(() => undefined);
          }
        }
      }
    };
  }, []);

  useEffect(() => {
      if (!conversationId$.value) {
          selectedGroupIds$.next([]);
          selectedDocIds$.next([]);
      }
  }, [conversationId$.value]);

  const value = useMemo(
    () => ({
      state,
      sendMessage,
      retryMessage,
      retryInNewChat,
      stopGeneration,
      updateSourcesFromUser,
      updateCollectionsFromUser,
      updateDocumentsFromUser,
      updateModelFromUser,
      loadConversationFromBackend,
      createNewConversation,
      activateVariant,
      exportToPdf,
      setInitialModel,
    }),
    [
      state,
      sendMessage,
      retryMessage,
      retryInNewChat,
      stopGeneration,
      updateSourcesFromUser,
      updateCollectionsFromUser,
      updateDocumentsFromUser,
      updateModelFromUser,
      loadConversationFromBackend,
      createNewConversation,
      activateVariant,
      exportToPdf,
      setInitialModel,
    ]
  );

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
};
